import { useApiClient } from '@/composables/api-client'
import { createItemStoreWrapper } from '~/utils/pinia/item-store-setup'
import type { FantasyDay, FantasyMemberCard } from '~/types/fantasy'

const createItemStore = createItemStoreWrapper<'id', FantasyDay>()

export const useFantasyDayStore = createItemStore(
  'fantasy-day',
  {
    path: id => `/api/v1/fantasy/season/days/${id}`,
    query: {
      expand: [
        'rewards.resource',
        'memberStatus',
        'positions',
        'slots.cardMember.currentCard.player.team',
        'slots.cardMember.currentCard.labels'
      ]
    }
  },
  context => {
    const selectedDayId = ref<number | null>(null)

    const dayId = computed(() => context.data.value?.id ?? null)
    const isLocked = computed(() =>
      context.data.value ? !context.data.value.can_set_slot : true
    )
    const isCompleted = computed(
      () => context.data.value?.is_completed ?? false
    )
    const canClaim = computed(() => context.data.value?.can_claim ?? false)
    const isClaimed = computed(
      () => !!context.data.value?.memberStatus?.rewarded_at
    )
    const points = computed(() => context.data.value?.memberStatus?.points ?? 0)
    const topPosition = computed(
      () => context.data.value?.memberStatus?.top_position ?? null
    )
    const timestamp = computed(() => context.data.value?.date_timestamp ?? 0)
    const lockTimestamp = computed(
      () => context.data.value?.date_slot_lock ?? 0
    )
    const rewards = computed(() => context.data.value?.rewards ?? [])
    const positions = computed(() => context.data.value?.positions ?? [])
    const slots = computed(() => context.data.value?.slots ?? [])
    const isParticipated = computed(
      () => (context.data.value?.slots ?? []).length > 0
    )
    const winnersCount = computed(() => context.data.value?.winners_count ?? 0)

    const hasAvailableReward = computed(() => {
      if (!isCompleted.value || isClaimed.value) {
        return false
      }
      return rewards.value.some(item => item.is_available)
    })

    const selectDay = async (id: number | null) => {
      if (id === null) return
      if (selectedDayId.value === id) return
      selectedDayId.value = id
      await context.fetchItem(id)
    }

    const setCard = async ({
      dayId,
      positionId,
      card
    }: {
      dayId: number
      positionId: number
      card: FantasyMemberCard
    }) => {
      const apiClient = await useApiClient()
      await apiClient.post(`/api/v1/fantasy/season/days/${dayId}/slot`, {
        body: {
          card_member_id: card.id,
          position: positionId
        }
      })

      if (!context.data.value) return

      const slot = context.data.value.slots.find(
        slot => slot.position === positionId
      )
      if (slot) {
        slot.card_member_id = card.id
        slot.cardMember = card
      } else {
        context.data.value.slots.push({
          card_member_id: card.id,
          position: positionId,
          cardMember: card
        })
      }
    }

    const removeCard = async ({
      dayId,
      positionId
    }: {
      dayId: number
      positionId: number
    }) => {
      const apiClient = await useApiClient()
      await apiClient.delete(
        `/api/v1/fantasy/season/days/${dayId}/slot/${positionId}`
      )

      if (!context.data.value) return

      const index = context.data.value.slots.findIndex(
        slot => slot.position === positionId
      )
      if (index !== -1) {
        context.data.value.slots.splice(index, 1)
      }
    }

    return {
      selectedDayId,
      dayId,
      isLocked,
      isCompleted,
      canClaim,
      isClaimed,
      points,
      topPosition,
      timestamp,
      lockTimestamp,
      rewards,
      positions,
      slots,
      isParticipated,
      winnersCount,
      hasAvailableReward,
      selectDay,
      setCard,
      removeCard
    }
  }
)
